"use client";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { FaCreditCard, FaIdeal, FaPaypal, FaChevronDown, FaCircleCheck } from "react-icons/fa6";
import KlarnaIcon from "../gfx/icons/klarna";
import { Label } from "../ui/label";

export default function PaymentSelect({
    paymentMethod,
    total,
    selectPaymentMethodAction,
    surchargeHint = "zzgl. 2,- € Transaktionsgebühr",
}: {
    paymentMethod: string | undefined;
    total: number;
    selectPaymentMethodAction: (e: string) => void;
    surchargeHint?: string;
}) {
    const [paySelectOpen, setPaySelectOpen] = useState(false);
    const [otherPaymentMethod, setOtherPaymentMethod] = useState<string>("");

    const PaymentOption = ({ 
        value, 
        icon, 
        label, 
        selected,
        onClick 
    }: { 
        value: string; 
        icon: React.ReactNode; 
        label: string; 
        selected: boolean;
        onClick: () => void;
    }) => (
        <Label
            htmlFor={value}
            onClick={onClick}
            className={`
                group flex cursor-pointer items-center gap-4 rounded-xl border-2 p-4 transition-all
                ${selected 
                    ? 'border-amber-400 bg-amber-50' 
                    : 'border-slate-200 bg-white hover:border-amber-200 hover:bg-amber-50/50'
                }
            `}
        >
            <RadioGroupItem value={value} id={value} className="sr-only" />
            <div className={`
                flex h-12 w-12 items-center justify-center rounded-xl transition-colors
                ${selected ? 'bg-amber-400 text-white' : 'bg-slate-100 text-slate-600 group-hover:bg-amber-100'}
            `}>
                {icon}
            </div>
            <div className="flex-1">
                <p className="font-semibold text-slate-800">{label}</p>
                <p className="text-xs text-slate-500">{surchargeHint}</p>
            </div>
            {selected && (
                <FaCircleCheck className="h-5 w-5 text-amber-500" />
            )}
        </Label>
    );

    return (
        <RadioGroup
            value={paymentMethod}
            onValueChange={(value) => selectPaymentMethodAction(value)}
            className="space-y-3"
        >
            <PaymentOption
                value="card"
                icon={<FaCreditCard className="h-5 w-5" />}
                label="Kartenzahlung"
                selected={paymentMethod === "card"}
                onClick={() => {
                    selectPaymentMethodAction("card");
                    setOtherPaymentMethod("");
                }}
            />

            <PaymentOption
                value="paypal"
                icon={<FaPaypal className="h-5 w-5" />}
                label="PayPal"
                selected={paymentMethod === "paypal"}
                onClick={() => {
                    selectPaymentMethodAction("paypal");
                    setOtherPaymentMethod("");
                }}
            />

            {/* Other payment methods */}
            <Label
                htmlFor={otherPaymentMethod}
                onClick={() => setPaySelectOpen(!paySelectOpen)}
                className={`
                    group flex cursor-pointer items-center gap-4 rounded-xl border-2 p-4 transition-all
                    ${otherPaymentMethod && paymentMethod === otherPaymentMethod
                        ? 'border-amber-400 bg-amber-50' 
                        : 'border-slate-200 bg-white hover:border-amber-200 hover:bg-amber-50/50'
                    }
                `}
            >
                <RadioGroupItem value={otherPaymentMethod} id={otherPaymentMethod} className="sr-only" />
                <div className={`
                    flex h-12 w-12 items-center justify-center rounded-xl transition-colors
                    ${otherPaymentMethod && paymentMethod === otherPaymentMethod 
                        ? 'bg-amber-400 text-white' 
                        : 'bg-slate-100 text-slate-600 group-hover:bg-amber-100'
                    }
                `}>
                    {otherPaymentMethod === 'klarna' ? (
                        <KlarnaIcon className="h-5 w-8" />
                    ) : otherPaymentMethod === 'ideal' ? (
                        <FaIdeal className="h-5 w-5" />
                    ) : (
                        <FaChevronDown className="h-4 w-4" />
                    )}
                </div>
                <div className="flex-1">
                    <Select
                        value={otherPaymentMethod}
                        onValueChange={(value) => {
                            setOtherPaymentMethod(value);
                            selectPaymentMethodAction(value);
                        }}
                        onOpenChange={() => setPaySelectOpen(!paySelectOpen)}
                        open={paySelectOpen}
                    >
                        <SelectTrigger className="h-auto border-0 bg-transparent p-0 text-left font-semibold text-slate-800 shadow-none focus:ring-0">
                            <SelectValue placeholder="Weitere Anbieter" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                            <SelectItem value="klarna">
                                <div className="flex items-center gap-2">
                                    <KlarnaIcon className="h-4 w-8" />
                                    <span>Klarna</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="ideal">
                                <div className="flex items-center gap-2">
                                    <FaIdeal className="h-4 w-4" />
                                    <span>iDeal</span>
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">{surchargeHint}</p>
                </div>
                {otherPaymentMethod && paymentMethod === otherPaymentMethod && (
                    <FaCircleCheck className="h-5 w-5 text-amber-500" />
                )}
            </Label>
        </RadioGroup>
    );
}
